import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import InputText from 'components/library/Input/InputText/InputText';

import sharedStyles from '../Confirmation.module.css';
import styles from './ConfirmationSociety.module.css';

import isEmpty from 'utils/isEmpty';
import * as statuses from 'store/states';
import LocationInput from 'components/library/Input/LocationInput';
import Dropdown from 'components/Dropdown';
import useOutsideClickAlert from 'components/library/hook/clickOutside';
import { Society, SocietyState } from 'store/features/society/types';
import { AppDispatch } from 'store/types';

type SocietyList = {
  status: typeof statuses[keyof typeof statuses];
  data: Society[];
}

type ErrorForm = {
  societyAddress?: string;
  siren?: string;
  rcs?: string;
  nameSociety?: string;
  societyLegal?: string;
  role?: string;
}

interface Props {
  nameSociety: string,
  countrySociety: string,
  siren: string,
  id?: number | string,
  societyAddress: string,
  societyCity: string,
  societyZipCode: string,
  societyLegal: string,
  role: string,
  rcs: string,
  errors: ErrorForm,
  children: (isComplete: boolean, values: Partial<SocietyState>) => React.ReactNode,
  societyList: SocietyList,
  actions: {
    getSocietyList: () => void,
    setSocietyChoice: (value: string) => void;
  },
}

export default function ConfirmationSociety({
  nameSociety, countrySociety, siren, id,
  societyAddress, societyCity, societyZipCode, societyLegal, role,
  rcs, errors, children, societyList, actions,
}: Props) {
  const [isComplete, setIsComplete] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const dropdown = useRef<HTMLDivElement>(null);

  useOutsideClickAlert(dropdown, () => setIsOpen(false));

  const LEGAL_FORMS = [
    t('companyCoordonate.legalForms.ei'), t('companyCoordonate.legalForms.sas'), t('companyCoordonate.legalForms.sarl'),
    t('companyCoordonate.legalForms.sci'), t('companyCoordonate.legalForms.sai')
  ];

  useEffect(() => {
    if (societyList.status === 'IDLE') {
      actions.getSocietyList();
    }
  }, [actions, societyList]);

  useEffect(() => {
    const toCheck = [
      nameSociety, countrySociety, role, rcs,
      siren, societyAddress, societyZipCode, societyCity
    ];
    const completed = toCheck.map((i) => !isEmpty(i)).reduce((c, a) => c && a, true);
    setIsComplete(completed);
  }, [nameSociety, countrySociety, siren, societyAddress,
    societyZipCode, societyCity, societyLegal, role, rcs]);

  const setLocation = (name: string, value: string, isSociety: boolean = false) => {
    if (isSociety) {
      if (name === 'address') {
        dispatch({ type: 'UPDATE_SOCIETY_ADDRESS', payload: value });
      }
      else if (name === 'city') {
        dispatch({ type: 'UPDATE_SOCIETY_CITY', payload: value });
      }
      else if (name === 'zipCode') {
        dispatch({ type: 'UPDATE_SOCIETY_ZIP_CODE', payload: value });
      }
      else if (name === 'country') {
        dispatch({ type: 'UPDATE_COUNTRY_SOCIETY', payload: value });
      }
    } else {
      if (name === 'address') {
        dispatch({ type: 'UPDATE_REPRESENTATIVE_ADDRESS', payload: value });
      }
      else if (name === 'city') {
        dispatch({ type: 'UPDATE_REPRESENTATIVE_CITY', payload: value });
      }
      else if (name === 'zipCode') {
        dispatch({ type: 'UPDATE_REPRESENTATIVE_ZIP_CODE', payload: value });
      }
    }
  }

  const handleSocietyLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocation(e.target.name, e.target.value, true);
  }

  return (
    <>
      <h2 className={sharedStyles.title}>
        {t('companyCoordonate.title1')}
      </h2>
      <p className={sharedStyles.description}>
        {t('companyCoordonate.description')}
      </p>
      <form data-cy="confirmation-form-society">
        {
          ((societyList.status === 'SUCCESS') && (societyList.data.length > 0))
          && (
            <Dropdown
              label={t('companyCoordonate.companyChoice')}
              name="companyChoice"
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                actions.setSocietyChoice(e.target.value);
              }}
              options={[...societyList.data.map((society: Society) => ({ value: society.id?.toString() || 'empty', name: society.society_name })), { value: 'empty', name: t('companyCoordonate.newCompany') }]}
              value={id && id.toString() || 'empty'}
            />
          )
        }
        <InputText
          label={t('companyCoordonate.siren')}
          placeholder={t('companyCoordonate.siren')}
          name="siren"
          valueInput={siren}
          onTextChange={(e: React.ChangeEvent<HTMLInputElement>) => dispatch({ type: 'UPDATE_SIREN', payload: e.target.value })}
          state={(errors?.siren) ? 'ERROR' : 'DEFAULT'}
          hint={errors?.siren}
          maxLength={30}
        />
        <InputText
          label={t('companyCoordonate.rcs')}
          placeholder={t('companyCoordonate.rcs')}
          name="rcs"
          valueInput={rcs}
          onTextChange={(e) => dispatch({ type: 'UPDATE_RCS', payload: e.target.value })}
          state={(errors?.rcs) ? 'ERROR' : 'DEFAULT'}
          hint={errors?.rcs}
          maxLength={128}
        />
        <InputText
          label={t('companyCoordonate.name')}
          placeholder={t('companyCoordonate.name')}
          name="society-name"
          valueInput={nameSociety}
          onTextChange={(e) => dispatch({ type: 'UPDATE_NAME_SOCIETY', payload: e.target.value })}
          state={(errors?.nameSociety) ? 'ERROR' : 'DEFAULT'}
          hint={errors?.nameSociety}
          maxLength={128}
        />
        <div className={styles.relative} ref={dropdown}>
          <InputText
            label={t('companyCoordonate.legalForm')}
            placeholder={t('companyCoordonate.legalForm')}
            valueInput={societyLegal}
            onTextChange={(e) => dispatch({ type: 'UPDATE_LEGAL_SOCIETY', payload: e.target.value })}
            onClick={() => setIsOpen(true)}
            state={(errors?.societyLegal) ? 'ERROR' : 'DEFAULT'}
            hint={errors?.societyLegal}
            maxLength={128}
            id="legalForm"
            name="legalForm"
            list="legalForms"
          />
          {
            (isOpen) && (
              <div
                className={styles.legal}
              >
                {
                  LEGAL_FORMS
                    .filter((legal) => legal.toLowerCase().includes(societyLegal.toLowerCase()))
                    .map((legal) => (
                      <button
                        type="button"
                        data-cy={`legal-form-${legal}`}
                        name={legal}
                        onClick={() => {
                          dispatch({ type: 'UPDATE_LEGAL_SOCIETY', payload: legal });
                          setIsOpen(false);
                        }}
                      >
                        {legal}
                      </button>
                    ))
                }
              </div>
            )
          }
        </div>
        <InputText
          label={t('companyCoordonate.role')}
          placeholder={t('companyCoordonate.role')}
          valueInput={role}
          onTextChange={(e: React.ChangeEvent<HTMLInputElement>) => dispatch({ type: 'UPDATE_ROLE', payload: e.target.value })}
          state={(errors?.role) ? 'ERROR' : 'DEFAULT'}
          hint={errors?.role}
          maxLength={128}
          name="role"
        />
        <LocationInput
          values={{ address: societyAddress, city: societyCity, zipCode: societyZipCode, country: countrySociety }}
          handleChange={handleSocietyLocationChange}
          setNewValues={(name, value) => setLocation(name, value, true)}
          state={(errors?.societyAddress) ? 'ERROR' : 'DEFAULT'}
          hint={errors?.societyAddress}
          showCountry
          dataCy="society-location"
        />
        {
          children(
            isComplete,
            {
              nameSociety, countrySociety, siren,
              societyAddress, societyCity, societyZipCode, societyLegal, role,
              rcs,
            }
          )
        }
      </form>
    </>
  );
}
